import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [show2, setShow2] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    if (!token) return toast.error("Missing reset token — open the link from your email.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="reset-page" className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md bg-white border border-scu-smoke/15 rounded-md p-8">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} className="h-12" alt="SCU" />
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-scu-purple font-bold">Parent Account</div>
            <div className="font-bebas text-3xl tracking-wide text-scu-obsidian leading-none">Reset Password</div>
          </div>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <PwField label="New password" testid="reset-pw" value={pw} onChange={setPw} show={show} toggle={() => setShow((s) => !s)} />
          <PwField label="Confirm new password" testid="reset-pw2" value={pw2} onChange={setPw2} show={show2} toggle={() => setShow2((s) => !s)} />
          <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full h-11 bg-scu-purple hover:bg-scu-electric text-white font-bold uppercase tracking-widest">
            {loading ? "Updating…" : "Update Password"}
          </Button>
        </form>
        <div className="mt-6 text-sm text-center text-scu-smoke">
          <Link to="/login" data-testid="reset-back-login" className="text-scu-purple hover:text-scu-electric font-bold">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}

function PwField({ label, testid, value, onChange, show, toggle }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-scu-smoke font-bold">{label}</Label>
      <div className="relative">
        <input
          data-testid={testid}
          type={show ? "text" : "password"}
          required
          minLength={6}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-10 text-base shadow-sm focus:outline-none focus:ring-1 focus:ring-ring md:text-sm"
        />
        <button data-testid={`${testid}-toggle`} type="button" onClick={toggle} className="absolute right-2 top-1/2 -translate-y-1/2 text-scu-smoke hover:text-scu-purple" aria-label="Toggle password visibility">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
